// Real API service para conectar con el backend Django
const API_BASE_URL =
  "https://snappin-backend-django-3172e7dd3aff.herokuapp.com/api";

// Error messages fallbacks (English)
const ERROR_MESSAGES = {
  invalidData: "Invalid data provided",
  unauthorized: "Unauthorized access",
  forbidden: "Forbidden access",
  notFound: "Resource not found",
  internalServerError: "Internal server error",
  networkError: "Network error. Please check your connection",
  loginError: "Login failed",
  registerError: "Registration failed",
  googleLoginError: "Google login failed",
  fetchPhotosError: "Failed to fetch photos",
  failedDeletePhoto: "Failed to delete photo",
  createEventError: "Failed to create event",
  userIdRequired: "User ID is required",
  invalidUserIdFormat: "Invalid user ID format",
  errorGettingEvents: "Error getting events",
  errorGettingEvent: "Error getting event",
  eventIdRequired: "Event ID is required",
  errorDeletingEvent: "Error deleting event",
  failedUpgradePremiun: "Failed to upgrade to premium",
  failedDowngradePremiun: "Failed to downgrade from premium",
  enhancePhotoError: "Failed to enhance photo with AI",
  premiumRequiredForAI:
    "Premium subscription required to use AI photo enhancement",
};

// Custom error class for API errors
export class ApiError extends Error {
  constructor(message, status, data = null) {
    super(message);
    this.name = "ApiError";
    this.status = status;
    this.data = data;
  }
}

export const apiService = {
  async makeRequest(endpoint, options = {}) {
    const url = `${API_BASE_URL}${endpoint}`;

    const config = {
      headers: {
        "Content-Type": "application/json",
        ...options.headers,
      },
      credentials: "include", // Incluir cookies para sesiones
      ...options,
    };

    if (options.body && typeof options.body === "object") {
      const sanitizedBody = { ...options.body };
      if (sanitizedBody.password) {
        sanitizedBody.password = "[FILTERED]";
      }
      if (sanitizedBody.repeatPassword) {
        sanitizedBody.repeatPassword = "[FILTERED]";
      }
      config.body = JSON.stringify(options.body);
    }

    try {
      const response = await fetch(url, config);

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));

        // Handle different HTTP status codes
        switch (response.status) {
          case 400:
            throw new ApiError(ERROR_MESSAGES.invalidData, 400, errorData);
          case 401:
            throw new ApiError(ERROR_MESSAGES.unauthorized, 401, errorData);
          case 403:
            throw new ApiError(ERROR_MESSAGES.forbidden, 403, errorData);
          case 404:
            throw new ApiError(ERROR_MESSAGES.notFound, 404, errorData);
          case 500:
            throw new ApiError(
              ERROR_MESSAGES.internalServerError,
              500,
              errorData
            );
          default:
            throw new ApiError(
              errorData.message || `Error HTTP: ${response.status}`,
              response.status,
              errorData
            );
        }
      }

      return await response.json();
    } catch (error) {
      if (error instanceof ApiError) {
        throw error;
      }

      // Handle network errors or other fetch errors
      if (error.name === "TypeError" && error.message.includes("fetch")) {
        throw new ApiError(ERROR_MESSAGES.networkError, 0, {
          original: error.message,
        });
      }

      // Generic error fallback
      throw new ApiError(error.message || "Unknown error", 0, {
        original: error.message,
      });
    }
  },

  // Autenticación
  async login({ email, password }) {
    try {
      const response = await this.makeRequest("/login/", {
        method: "POST",
        body: { email, password },
      });

      // Guardar datos del usuario en localStorage
      if (response.user) {
        localStorage.setItem("user", JSON.stringify(response.user));
        localStorage.setItem("isAuthenticated", "true");
      }

      return response.user;
    } catch (error) {
      if (error instanceof ApiError) {
        throw error;
      }
      throw new ApiError(ERROR_MESSAGES.loginError, 0);
    }
  },

  async register({ username, email, password, repeatPassword }) {
    try {
      const response = await this.makeRequest("/register/", {
        method: "POST",
        body: {
          username,
          email,
          password,
          repeat_password: repeatPassword,
        },
      });

      // Guardar datos del usuario en localStorage
      if (response.user) {
        localStorage.setItem("user", JSON.stringify(response.user));
        localStorage.setItem("isAuthenticated", "true");
      }

      return response.user;
    } catch (error) {
      if (error instanceof ApiError) {
        throw error;
      }
      throw new ApiError(ERROR_MESSAGES.registerError, 0);
    }
  },

  async googleLogin({ credential, userData }) {
    try {
      const response = await this.makeRequest("/google-login/", {
        method: "POST",
        body: {
          credential,
          google_id: userData.googleId,
          email: userData.email,
          name: userData.name,
          picture: userData.picture,
          email_verified: userData.emailVerified,
        },
      });

      // Guardar datos del usuario en localStorage
      if (response.user) {
        localStorage.setItem("user", JSON.stringify(response.user));
        localStorage.setItem("isAuthenticated", "true");
      }

      return response.user;
    } catch (error) {
      if (error instanceof ApiError) {
        throw error;
      }
      throw new ApiError(ERROR_MESSAGES.googleLoginError, 0);
    }
  },

  // Obtener usuario actual
  getCurrentUser() {
    try {
      const user = localStorage.getItem("user");
      const isAuthenticated = localStorage.getItem("isAuthenticated");

      if (isAuthenticated === "true" && user) {
        return JSON.parse(user);
      }
      return null;
    } catch (error) {
      return null;
    }
  },

  // Cerrar sesión
  logout() {
    localStorage.removeItem("user");
    localStorage.removeItem("isAuthenticated");
  },

  // Verificar si el usuario está autenticado
  isAuthenticated() {
    return localStorage.getItem("isAuthenticated") === "true";
  },

  // Photo upload methods
  uploadEventPhoto: async (eventId, file, metadata = {}) => {
    try {
      const formData = new FormData();
      formData.append("file", file);
      formData.append("event_id", eventId);

      // Send uploader name in form data instead of header
      if (metadata.uploaderName) {
        formData.append("uploader_name", metadata.uploaderName);
      }

      const headers = {};

      if (metadata.width) headers["Picture-Width"] = metadata.width;
      if (metadata.height) headers["Picture-Height"] = metadata.height;
      if (metadata.device) headers["Picture-Device"] = metadata.device;
      if (metadata.orientation)
        headers["Picture-Orientation"] = metadata.orientation;

      const response = await fetch(`${API_BASE_URL}/upload-event-photo/`, {
        method: "POST",
        body: formData,
        headers: headers,
        credentials: "include",
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(
          data.message || `HTTP ${response.status}: Upload failed`
        );
      }

      return data;
    } catch (error) {
      throw error;
    }
  },

  getEventPhotos: async (eventId, page = 0, limit = 30) => {
    try {
      const response = await fetch(
        `${API_BASE_URL}/get-event-photos?eventId=${eventId}&page=${page}&limit=${limit}`,
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
          },
          credentials: "include",
        }
      );

      const data = await response.json();

      if (!response.ok) {
        throw new Error(ERROR_MESSAGES.fetchPhotosError);
      }

      return data;
    } catch (error) {
      throw error;
    }
  },

  deleteEventPhoto: async (photoId, eventId) => {
    const response = await fetch(`${API_BASE_URL}/delete-event-photo/`, {
      method: "DELETE",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        photoId,
        eventId,
      }),
      credentials: "include",
    });

    const data = await response.json();
    if (!response.ok) {
      throw new Error(ERROR_MESSAGES.failedDeletePhoto);
    }
    return data;
  },

  enhancePhotoWithAI: async (
    photoId,
    eventId,
    enhancementType = "professional",
    prompt,
    signal
  ) => {
    if (enhancementType === "custom") {
      // Fire-and-forget solo para custom
      fetch(`${API_BASE_URL}/enhance-photo-with-ai/`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          photo_id: photoId,
          event_id: eventId,
          enhancement_type: enhancementType,
          ...(prompt ? { prompt } : {}),
          ...(prompt || enhancementType === "custom" ? { use_api: true } : {}),
        }),
        credentials: "include",
        ...(signal ? { signal } : {}),
      }).catch((error) => {
        // Solo loguear el error, no interrumpir el flujo
        console.error("AI enhancement error (fire-and-forget):", error);
      });
      // Devolver éxito inmediato (simulado)
      return {
        success: true,
        enhancementType: enhancementType,
        applied_custom_mode: enhancementType === "custom",
      };
    } else {
      // Comportamiento original para los demás casos
      try {
        const response = await fetch(`${API_BASE_URL}/enhance-photo-with-ai/`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            photo_id: photoId,
            event_id: eventId,
            enhancement_type: enhancementType,
            ...(prompt ? { prompt } : {}),
            ...(prompt || enhancementType === "custom"
              ? { use_api: true }
              : {}),
          }),
          credentials: "include",
          ...(signal ? { signal } : {}),
        });

        const data = await response.json();

        if (!response.ok) {
          if (response.status === 402) {
            throw new Error(ERROR_MESSAGES.premiumRequiredForAI);
          }
          throw new Error(data.message || ERROR_MESSAGES.enhancePhotoError);
        }
        // Always return ai_applied and reason for UI
        return {
          ...data,
          ai_applied: data.ai_applied,
          reason: data.reason,
        };
      } catch (error) {
        console.error("AI enhancement error:", error);
        throw error;
      }
    }
  },

  async createEvent(eventData, userId) {
    try {
      const response = await this.makeRequest("/create-event/", {
        method: "POST",
        body: {
          name: eventData.name,
          description: eventData.description,
          date: eventData.date,
          time: eventData.time,
          location: eventData.location,
          userId: userId, // Enviamos el userId desde el frontend
        },
      });

      if (response.event) {
        return response.event;
      }

      throw new ApiError("No event data received", 500);
    } catch (error) {
      if (error instanceof ApiError) {
        throw error;
      }
      throw new ApiError(ERROR_MESSAGES.createEventError, 0);
    }
  },

  async getEvents(userId) {
    if (!userId) {
      throw new ApiError(ERROR_MESSAGES.userIdRequired, 400);
    }

    // Validar que tenemos un número válido
    const numericUserId = Number(userId);
    if (isNaN(numericUserId)) {
      throw new ApiError(ERROR_MESSAGES.invalidUserIdFormat, 400);
    }

    try {
      const response = await this.makeRequest(
        `/get-events/?userId=${numericUserId}`,
        {
          method: "GET",
        }
      );

      if (response.success) {
        return response.events || [];
      } else {
        throw new ApiError(ERROR_MESSAGES.errorGettingEvents, 500, response);
      }
    } catch (error) {
      if (error instanceof ApiError) {
        throw error;
      }
      throw new ApiError(ERROR_MESSAGES.errorGettingEvents, 0);
    }
  },

  async getEvent(eventId) {
    try {
      if (!eventId) {
        throw new ApiError("Event ID is required", 400);
      }

      const response = await this.makeRequest(
        `/get-event/?eventId=${eventId}`,
        {
          method: "GET",
        }
      );

      if (!response.success) {
        throw new ApiError(ERROR_MESSAGES.errorGettingEvent, 0);
      }

      return response.event;
    } catch (error) {
      if (error instanceof ApiError) {
        throw error;
      }
      throw new ApiError(ERROR_MESSAGES.errorGettingEvent, 0);
    }
  },

  async deleteEvent(eventId, userId) {
    try {
      if (!eventId) {
        throw new ApiError(ERROR_MESSAGES.eventIdRequired, 400);
      }

      if (!userId) {
        throw new ApiError(ERROR_MESSAGES.userIdRequired, 400);
      }

      const response = await this.makeRequest("/delete-event/", {
        method: "DELETE",
        body: {
          eventId,
          userId,
        },
      });

      if (!response.success) {
        throw new ApiError(ERROR_MESSAGES.errorDeletingEvent, 0);
      }

      return response;
    } catch (error) {
      if (error instanceof ApiError) {
        throw error;
      }
      throw new ApiError(ERROR_MESSAGES.errorDeletingEvent, 0);
    }
  },

  async updateEvent(eventData, userId) {
    try {
      const response = await this.makeRequest("/update-event/", {
        method: "POST",
        body: {
          eventId: eventData.id,
          userId,
          name: eventData.name,
          description: eventData.description || "",
          date: eventData.date, // YYYY-MM-DD
          time: eventData.time, // HH:MM
          location: eventData.location || "",
        },
      });

      if (response.success) {
        return response.event;
      }
      throw new ApiError(response.message || "Failed to update event", 400);
    } catch (error) {
      if (error instanceof ApiError) throw error;
      throw new ApiError("Failed to update event", 0);
    }
  },

  // Premium API methods
  upgradeToPremium: async (userId, paymentMethod = "manual") => {
    try {
      const response = await fetch(`${API_BASE_URL}/upgrade-to-premium/`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          userId,
          paymentMethod,
        }),
        credentials: "include",
      });

      const data = await response.json();

      if (!response.ok) {
        // Use the specific error message from the server
        throw new Error(data.message || ERROR_MESSAGES.failedUpgradePremiun);
      }

      return data;
    } catch (error) {
      throw error;
    }
  },

  downgradeFromPremium: async (userId) => {
    try {
      const response = await fetch(`${API_BASE_URL}/downgrade-from-premium/`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          userId,
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(ERROR_MESSAGES.failedDowngradePremiun);
      }

      return data;
    } catch (error) {
      throw error;
    }
  },

  getPremiumStatus: async (userId) => {
    try {
      const response = await fetch(
        `${API_BASE_URL}/get-premium-status/?userId=${userId}`,
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
          },
        }
      );

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || "Failed to get premium status");
      }

      return data;
    } catch (error) {
      throw error;
    }
  },

  // PayPal payment processing
  processPayPalPayment: async (paymentData) => {
    try {
      const response = await fetch(`${API_BASE_URL}/process-paypal-payment/`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(paymentData),
        credentials: "include",
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || "Failed to process PayPal payment");
      }

      return data;
    } catch (error) {
      throw error;
    }
  },

  // Server-side PayPal flow
  createPayPalOrder: async ({ plan = "monthly", userId } = {}) => {
    // Try to obtain userId from argument, else from locally stored user
    if (!userId) {
      try {
        const stored = localStorage.getItem("user");
        if (stored) {
          const parsed = JSON.parse(stored);
          if (parsed?.id) userId = parsed.id;
        }
      } catch (e) {
        // ignore JSON errors
      }
    }

    const payload = { plan, ...(userId ? { userId } : {}) };
    const response = await fetch(`${API_BASE_URL}/paypal/create-order/`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      credentials: "include",
      body: JSON.stringify(payload),
    });
    const data = await response.json();
    if (!response.ok || !data?.success) {
      throw new Error(data?.message || "Failed to create PayPal order");
    }
    return data;
  },

  capturePayPalOrder: async ({ orderID, plan, userId } = {}) => {
    if (!userId) {
      try {
        const stored = localStorage.getItem("user");
        if (stored) {
          const parsed = JSON.parse(stored);
          if (parsed?.id) userId = parsed.id;
        }
      } catch (e) {}
    }
    const payload = { orderID, plan, ...(userId ? { userId } : {}) };
    const response = await fetch(`${API_BASE_URL}/paypal/capture-order/`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      credentials: "include",
      body: JSON.stringify(payload),
    });
    const data = await response.json();
    if (!response.ok || !data?.success) {
      throw new Error(data?.message || "Failed to capture PayPal order");
    }
    return data;
  },

  // Cleanup expired events
  cleanupExpiredEvents: async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/cleanup-expired-events/`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        credentials: "include",
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || "Failed to cleanup expired events");
      }

      return data;
    } catch (error) {
      throw error;
    }
  },

  // Contact form submission
  async submitContactForm(contactData) {
    try {
      const response = await this.makeRequest("/contact/", {
        method: "POST",
        body: {
          name: contactData.name,
          email: contactData.email,
          subject: contactData.subject,
          message: contactData.message,
        },
      });

      return response;
    } catch (error) {
      if (error instanceof ApiError) {
        throw error;
      }
      throw new ApiError("Failed to submit contact form", 0);
    }
  },
};

export default apiService;
